import React, { useEffect, useRef, useState, type JSX } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { IdxStatus } from '@okta/okta-auth-js';
import OktaSignIn from '@okta/okta-signin-widget';
import logo from '../assets/images/eaton-logo1.png';
import oktaConfig from '../oktaConfig';
import { useNavigate } from 'react-router-dom';
import { BackdropLoader, NeedHelpScreen, PrivacyPolicy } from './';
import { useGetOpenRealmSettingsQuery } from '@fiji/common/src/features/realmManagement/realmApi';
import { RootState, useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { getRealmNameFromSuffix } from '../utils/helpers';
import { ClickAwayListener, FormControl, MenuItem, Popper, Stack, Typography } from '@mui/material';
import { Language } from '@mui/icons-material';
import { useGetLanguagesQuery } from '@fiji/common/src/features/profile/profileApi';
import { Languages } from '@fiji/common/src/types';
import { selectLocale, setLocale } from '@fiji/common/src/features/localization/localizaitonSlice';
import { useGetOperTranslationsQuery } from '@fiji/common/src/features/localization/localizationApi';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE } from '@fiji/common/src/constants/cacheTimeout';
import { fijiToOktaLanguageMap } from '@fiji/common';
import { sanitizeRedirectPath } from '../utils/security';
import { useLazyGetRegisteredUserAgreementsPublicQuery } from '@fiji/common/src/features/userManagement/userApi';

/**
 * The above code is a TypeScript React component that renders a custom login form using the Okta
 * Sign-In Widget and includes additional features such as custom buttons for sign up, help, and
 * privacy policy.
 * @returns The `CustomLoginComponent` function returns a JSX element.
 */
export const CustomLoginComponent = (): JSX.Element => {
    const { oktaAuth, authState } = useOktaAuth();
    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation();
    const locale = useTypedSelector(selectLocale);
    const currentRealmName = useTypedSelector((state: RootState) => state.common.selectedRealm);
    const isSuperRealm = useTypedSelector((state: RootState) => state.common.isSuperRealm);
    const widgetRef = useRef(null);
    const widgetInstanceRef = useRef<OktaSignIn | null>(null);
    const hasStartedLoginFlowRef = useRef(false);
    const navigate = useNavigate();
    const policyRef = useRef<any>(null);

    const [widgetReady, setWidgetReady] = React.useState(false);
    const [isContactScreenOpen, setIsContactScreenOpen] = useState(false);
    const [isCheckingExistingSession, setIsCheckingExistingSession] = useState(true);
    const localizationCount = useTypedSelector((state: RootState) => state.common.isLocalizationCount);
    const globalLocale = window.localStorage.getItem('globalLocale');

    // OTP auto-verification state
    const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
    const [otpVerificationAttempted, setOtpVerificationAttempted] = useState(false);

    // Fetch otp and state from query params from email callback verification URI
    // Application should have http://localhost:8080/login as the email callback verification URI
    const queryParams = new URLSearchParams(window.location.search);
    const stateToken = queryParams.get('stateToken');
    const emailVerificationState = queryParams.get('state');
    const otp = queryParams.get('otp');
    const callbackFallback = queryParams.get('cb_fallback');

    // Check for stored state from previous session (for recovery)
    const storedState = sessionStorage.getItem('okta-transaction-state') ?? '';

    // Helper: wrap promise with timeout to prevent infinite hanging
    const withOtpTimeout = <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> =>
        new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                reject(new Error('OTP verification timeout'));
            }, timeoutMs);

            promise
                .then((result) => {
                    clearTimeout(timer);
                    resolve(result);
                })
                .catch((err) => {
                    clearTimeout(timer);
                    reject(err);
                });
        });

    // AUTO-VERIFY OTP: Try idx.proceed() before showing widget
    // This handles the case when user clicks magic link in same browser
    useEffect(() => {
        if (!otp || !emailVerificationState || callbackFallback) {
            return;
        }

        const isRealmLoginRoute = /^\/[^/]+\/login$/.test(window.location.pathname);
        if (!isRealmLoginRoute) {
            return;
        }

        const callbackUrl = new URL(`${window.location.origin}/login/callback`);
        callbackUrl.searchParams.set('state', emailVerificationState);
        callbackUrl.searchParams.set('otp', otp);
        window.location.replace(callbackUrl.toString());
    }, [otp, emailVerificationState, callbackFallback]);

    useEffect(() => {
        const autoVerifyOTP = async (): Promise<void> => {
            // Only attempt if we have otp and state, and haven't tried yet
            if (!otp || !emailVerificationState || otpVerificationAttempted || isVerifyingOTP) {
                if (otp || emailVerificationState) {
                    // eslint-disable-next-line no-console
                    console.info('[OTP-DEBUG] skip autoVerifyOTP', {
                        hasOtp: Boolean(otp),
                        hasState: Boolean(emailVerificationState),
                        otpVerificationAttempted,
                        isVerifyingOTP,
                    });
                }
                return;
            }

            setIsVerifyingOTP(true);
            setOtpVerificationAttempted(true);
            // eslint-disable-next-line no-console
            console.info('[OTP-DEBUG] autoVerifyOTP start', {
                hasOtp: Boolean(otp),
                statePrefix: emailVerificationState?.slice(0, 8),
            });

            try {
                const canProceed = oktaAuth.idx.canProceed({ state: emailVerificationState });
                // eslint-disable-next-line no-console
                console.info('[OTP-DEBUG] canProceed', {
                    canProceed,
                    statePrefix: emailVerificationState?.slice(0, 8),
                });
                if (!canProceed) {
                    // eslint-disable-next-line no-console
                    console.warn('[OTP-DEBUG] cannot proceed with saved transaction; trying stateHandle fallback');
                    try {
                        // Some org/policy flows can return a resumable state handle via `state`.
                        // Attempt direct proceed using stateHandle before falling back to widget UI.
                        const transaction = await withOtpTimeout(
                            oktaAuth.idx.proceed({ stateHandle: emailVerificationState, otp } as any),
                            8000
                        );
                        // eslint-disable-next-line no-console
                        console.info('[OTP-DEBUG] stateHandle fallback result', {
                            status: transaction?.status,
                            hasTokens: Boolean(transaction?.tokens),
                            hasNextStep: Boolean(transaction?.nextStep),
                        });
                        if (transaction?.status === IdxStatus.SUCCESS && transaction?.tokens) {
                            oktaAuth.tokenManager.setTokens(transaction.tokens);
                            const unsafeRedirectPath =
                                currentRealmName ||
                                sessionStorage.getItem('referrer') ||
                                window?.APP_CONFIG?.SUPER_REALM ||
                                'eaton';

                            // Sanitize the redirect path to prevent XSS attacks
                            const redirectPath = sanitizeRedirectPath(unsafeRedirectPath) || 'eaton';

                            // Additional safety: ensure we're constructing a valid URL
                            try {
                                const redirectUrl = new URL(`${window.location.origin}/${redirectPath}`);
                                // Verify the origin matches (defense in depth)
                                if (redirectUrl.origin === window.location.origin) {
                                    window.location.replace(redirectUrl.toString());
                                } else {
                                    console.error('[Security] Redirect origin mismatch, using default');
                                    window.location.replace(`${window.location.origin}/eaton`);
                                }
                            } catch (e) {
                                console.error('[Security] Invalid redirect URL, using default', e);
                                window.location.replace(`${window.location.origin}/eaton`);
                            }
                            return;
                        }
                    } catch (fallbackErr: any) {
                        // eslint-disable-next-line no-console
                        console.warn('[OTP-DEBUG] stateHandle fallback failed', {
                            message: fallbackErr?.message,
                            name: fallbackErr?.name,
                        });
                    }
                    setIsVerifyingOTP(false);
                    return;
                }

                // Call idx.proceed with timeout (8 seconds) to prevent infinite hanging
                const transaction = await withOtpTimeout(
                    oktaAuth.idx.proceed({ state: emailVerificationState, otp }),
                    8000
                );
                // eslint-disable-next-line no-console
                console.info('[OTP-DEBUG] idx.proceed result', {
                    status: transaction?.status,
                    hasTokens: Boolean(transaction?.tokens),
                    hasNextStep: Boolean(transaction?.nextStep),
                });

                if (transaction?.status === IdxStatus.SUCCESS && transaction?.tokens) {
                    // OTP verification successful - set tokens and redirect
                    oktaAuth.tokenManager.setTokens(transaction.tokens);

                    const unsafeRedirectPath =
                        currentRealmName ||
                        sessionStorage.getItem('referrer') ||
                        window?.APP_CONFIG?.SUPER_REALM ||
                        'eaton';

                    // Sanitize the redirect path to prevent XSS attacks
                    const redirectPath = sanitizeRedirectPath(unsafeRedirectPath) || 'eaton';

                    // Additional safety: ensure we're constructing a valid URL
                    try {
                        const redirectUrl = new URL(`${window.location.origin}/${redirectPath}`);
                        // Verify the origin matches (defense in depth)
                        if (redirectUrl.origin === window.location.origin) {
                            window.location.replace(redirectUrl.toString());
                        } else {
                            console.error('[Security] Redirect origin mismatch, using default');
                            window.location.replace(`${window.location.origin}/eaton`);
                        }
                    } catch (e) {
                        console.error('[Security] Invalid redirect URL, using default', e);
                        window.location.replace(`${window.location.origin}/eaton`);
                    }
                    return;
                }

                // Not successful - fall through to show widget
                // eslint-disable-next-line no-console
                console.warn('[OTP-DEBUG] idx.proceed did not return success with tokens');
                setIsVerifyingOTP(false);
            } catch (err: any) {
                // eslint-disable-next-line no-console
                console.error('[OTP-DEBUG] idx.proceed error', {
                    message: err?.message,
                    name: err?.name,
                });
                setIsVerifyingOTP(false);
            }
        };

        void autoVerifyOTP();
    }, [otp, emailVerificationState, oktaAuth, otpVerificationAttempted, isVerifyingOTP, currentRealmName]);

    const { data: realmSettings } = useGetOpenRealmSettingsQuery(getRealmNameFromSuffix(currentRealmName), {
        skip:
            !currentRealmName ||
            ['login', 'error', window?.APP_CONFIG?.SUPER_REALM, window.APP_CONFIG.SUPER_ADMIN].includes(
                currentRealmName
            ),
    }) as any;
    const {
        data: openTranslations,
        isLoading,
        isFetching,
    } = useGetOperTranslationsQuery(locale ?? globalLocale ?? DEFAULT_LANGUAGE, {
        refetchOnMountOrArgChange: true,
    });
    const { data: languagesData } = useGetLanguagesQuery<{
        data: Languages;
        isLoading: boolean;
    }>();
    const [getPublicAgreements] = useLazyGetRegisteredUserAgreementsPublicQuery();

    const prefetchPublicAgreements = async (): Promise<void> => {
        try {
            await getPublicAgreements({ realmPrefix: currentRealmName || 'platform' }, true);
        } catch {
            // Best-effort prefetch; registration/privacy flows handle missing data gracefully.
        }
    };

    const getPublicPrivacyPolicyUrl = async (): Promise<string | undefined> => {
        try {
            const response: any = await getPublicAgreements({ realmPrefix: currentRealmName || 'platform' }, true);
            const agreements = response?.data?.data?.agreements ?? [];
            return agreements.find((agreement: any) => agreement?.type === 'PRIVACY_NOTICE')?.url;
        } catch {
            return undefined;
        }
    };

    React.useEffect(() => {
        if (realmSettings?.data?.language && !window.localStorage.getItem('loginPageLocale')) {
            dispatch(setLocale(realmSettings?.data?.language));
            window.localStorage.setItem('tempLocale', realmSettings?.data?.language);
        }
    }, [realmSettings]);

    // Persist current state for recovery from interruptions
    React.useEffect(() => {
        if (emailVerificationState) {
            sessionStorage.setItem('okta-transaction-state', emailVerificationState);
        }
    }, [emailVerificationState]);

    // Store current realm for OTP email verification callback
    // This ensures users returning from email links are redirected to their correct realm
    React.useEffect(() => {
        if (currentRealmName && !['login', 'error'].includes(currentRealmName)) {
            sessionStorage.setItem('okta-verification-realm', currentRealmName);
            localStorage.setItem('lastUsedRealm', currentRealmName);
        }
    }, [currentRealmName]);

    /* The `useEffect` hook is used to perform side effects in a functional component. In this case, it
    is used to initialize and configure the Okta Sign-In Widget and handle any errors that occur
    during the authentication process. */

    const customButtons: any = [];

    const isPlatform = currentRealmName === 'platform';

    if ((!isSuperRealm && realmSettings?.data?.isRegistrationEnabled) || isPlatform) {
        customButtons.push({
            title: t('bluiRegistration:ACTIONS.DONT_HAVE_ACCOUNT'),
            className: 'btn-customAuth-ac',
        });
        customButtons.push({
            title: t('bluiRegistration:MESSAGES.SIGN_UP'),
            className: 'btn-customAuth',
            click: (): void => {
                void prefetchPublicAgreements();
                navigate(`/${isPlatform ? 'eaton' : currentRealmName}/register/create-account`);
            },
        });
    }
    customButtons.push({
        title: t('bluiRegistration:MESSAGES.CONTACT'),
        click: (): void => {
            setIsContactScreenOpen(true);
        },
    });
    customButtons.push({
        title: t('bluiRegistration:MESSAGES.PRIVACY'),
        click: async (): Promise<void> => {
            const privacyUrl = await getPublicPrivacyPolicyUrl();
            if (privacyUrl) {
                const openedWindow = window.open(privacyUrl, '_blank', 'noopener,noreferrer');
                if (openedWindow) {
                    openedWindow.focus();
                }
                return;
            }
            policyRef.current?.modalHandler?.(true);
        },
    });

    // Create a function to get the Okta language because localization keys are different in Okta
    const getOktaLanguage = (): string => {
        const lang = locale ?? window.localStorage.getItem('globaLocale') ?? DEFAULT_LANGUAGE;
        return fijiToOktaLanguageMap[lang as keyof typeof fijiToOktaLanguageMap] || 'en';
    };

    const oktaLang = getOktaLanguage();
    const oktaTranslations = openTranslations?.data ?? {};
    const withTimeout = <T,>(promise: Promise<T>, timeoutMs = 5000): Promise<T> =>
        new Promise<T>((resolve, reject) => {
            const timer = window.setTimeout(() => reject(new Error('Okta session bootstrap timeout')), timeoutMs);

            promise
                .then((value) => {
                    window.clearTimeout(timer);
                    resolve(value);
                })
                .catch((error) => {
                    window.clearTimeout(timer);
                    reject(error);
                });
        });

    React.useEffect(() => {
        if (authState?.isAuthenticated) {
            const unsafeRedirectPath =
                sessionStorage.getItem('referrer') ?? currentRealmName ?? window?.APP_CONFIG?.SUPER_REALM;
            // Sanitize the redirect path to prevent XSS attacks in React Router navigation
            const redirectPath = sanitizeRedirectPath(unsafeRedirectPath) || 'eaton';
            navigate(`/${redirectPath}`);
        }
    }, [authState?.isAuthenticated, navigate, currentRealmName]);

    React.useEffect(() => {
        let isMounted = true;

        const bootstrapExistingSession = async (): Promise<void> => {
            if (authState?.isAuthenticated || stateToken || oktaAuth.isLoginRedirect()) {
                if (isMounted) {
                    setIsCheckingExistingSession(false);
                }
                return;
            }

            try {
                const { tokens } = await withTimeout(
                    oktaAuth.token.getWithoutPrompt({
                        scopes: oktaConfig.scopes,
                    })
                );
                oktaAuth.tokenManager.setTokens(tokens);
            } catch {
                if (isMounted) {
                    setIsCheckingExistingSession(false);
                }
                return;
            }

            if (isMounted) {
                setIsCheckingExistingSession(false);
            }
        };

        void bootstrapExistingSession();

        return () => {
            isMounted = false;
        };
    }, [authState?.isAuthenticated, oktaAuth, stateToken]);

    useEffect((): void | (() => void) => {
        if (
            !widgetRef.current ||
            authState?.isAuthenticated ||
            isCheckingExistingSession ||
            widgetInstanceRef.current
        ) {
            return;
        }
        //
        const { issuer, clientId, redirectUri, scopes } = oktaConfig;

        /* The code is creating a new instance of the OktaSignIn class and configuring it with various
        options. */
        const widget = new OktaSignIn({
            authClient: oktaAuth,
            /**
             * Note: when using the Sign-In Widget for an OIDC flow, it still
             * needs to be configured with the base URL for your Okta Org. Here
             * we derive it from the given issuer for convenience.
             */
            baseUrl: issuer.split('/oauth2')[0],
            clientId,
            redirectUri,
            logo: realmSettings?.data?.avatarLogo ?? logo,
            i18n: {
                [oktaLang]: {
                    ...oktaTranslations,
                },
            },
            authParams: {
                // To avoid redirect do not set "pkce" or "display" here. OKTA-335945
                issuer,
                scopes,
                // pkce: false,
            },
            useInteractionCodeFlow: Boolean(oktaConfig.useInteractionCode),
            language: oktaLang,
            customButtons,
            features: {
                showPasswordToggleOnSignInPage: true,
            },
            ...(stateToken ? { stateToken } : {}),
            ...(emailVerificationState || storedState ? { state: emailVerificationState || storedState } : {}),
            ...(otp ? { otp } : {}),
        } as any);

        widgetInstanceRef.current = widget;

        /*  This method is used to display the sign-in form and redirect the user to the Okta authentication page for authentication. */
        if (!hasStartedLoginFlowRef.current) {
            hasStartedLoginFlowRef.current = true;
            const isEmailVerificationFlow = Boolean(otp && emailVerificationState);
            // eslint-disable-next-line no-console
            console.info('[OTP-DEBUG] widget flow selection', {
                isEmailVerificationFlow,
                hasOtp: Boolean(otp),
                hasState: Boolean(emailVerificationState),
                hasStateToken: Boolean(stateToken),
            });

            if (isEmailVerificationFlow) {
                widget
                    .showSignIn({ el: widgetRef.current })
                    .then((res: any) => {
                        // eslint-disable-next-line no-console
                        console.info('[OTP-DEBUG] widget showSignIn result', {
                            status: res?.status,
                            hasTokens: Boolean(res?.tokens),
                        });
                        // In email verification/remediation flows, widget can finish locally.
                        if (res?.status === 'SUCCESS' && res?.tokens) {
                            oktaAuth.tokenManager.setTokens(res.tokens);
                            const unsafeRedirectPath =
                                currentRealmName ||
                                sessionStorage.getItem('referrer') ||
                                window?.APP_CONFIG?.SUPER_REALM ||
                                'eaton';

                            // Sanitize the redirect path to prevent XSS attacks
                            const redirectPath = sanitizeRedirectPath(unsafeRedirectPath) || 'eaton';

                            // Additional safety: ensure we're constructing a valid URL
                            try {
                                const redirectUrl = new URL(`${window.location.origin}/${redirectPath}`);
                                // Verify the origin matches (defense in depth)
                                if (redirectUrl.origin === window.location.origin) {
                                    window.location.replace(redirectUrl.toString());
                                } else {
                                    console.error('[Security] Redirect origin mismatch, using default');
                                    window.location.replace(`${window.location.origin}/eaton`);
                                }
                            } catch (e) {
                                console.error('[Security] Invalid redirect URL, using default', e);
                                window.location.replace(`${window.location.origin}/eaton`);
                            }
                            return;
                        }
                        // eslint-disable-next-line no-console
                        console.warn('[OTP-DEBUG] widget showSignIn returned non-success');
                        hasStartedLoginFlowRef.current = false;
                    })
                    .catch((err: any) => {
                        // eslint-disable-next-line no-console
                        console.error('[OTP-DEBUG] widget showSignIn error', {
                            message: err?.message,
                            name: err?.name,
                        });
                        hasStartedLoginFlowRef.current = false;
                    });
            } else {
                widget.showSignInAndRedirect({ el: widgetRef.current }).catch((err: any) => {
                    // eslint-disable-next-line no-console
                    console.error('[OTP-DEBUG] widget showSignInAndRedirect error', {
                        message: err?.message,
                        name: err?.name,
                    });
                    hasStartedLoginFlowRef.current = false;
                });
            }
        }

        widget.on('afterRender', () => {
            setWidgetReady(true);
        });

        return () => {
            widget.remove();
            widgetInstanceRef.current = null;
            hasStartedLoginFlowRef.current = false;
            setWidgetReady(false);
        };
    }, [
        oktaAuth,
        authState?.isAuthenticated,
        isCheckingExistingSession,
        oktaTranslations,
        localizationCount,
        oktaLang,
        realmSettings?.data?.avatarLogo,
        realmSettings?.data?.isRegistrationEnabled,
    ]);

    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const handleIconClick = (event: any): void => {
        setAnchorEl(event.currentTarget);
        setOpen((prev) => !prev);
    };

    const handleClose = (): void => {
        setOpen(false);
    };

    const getClassName = (superRealm: boolean, avatarLogo: boolean): string => {
        if (superRealm) {
            return getSuperRealmClassName(avatarLogo);
        }
        return getNonSuperRealmClassName(avatarLogo);
    };

    const getSuperRealmClassName = (hasAvatarLogo: boolean): string =>
        hasAvatarLogo ? '' : 'super-realm-widget login-wrapper';

    const getNonSuperRealmClassName = (hasAvatarLogo: boolean): string =>
        hasAvatarLogo ? 'login-widget login-wrapper logo' : 'login-widget login-wrapper';

    // Show loading while auto-verifying OTP
    if (isVerifyingOTP) {
        return <BackdropLoader isOpen={true} showDefault={false} />;
    }

    return (
        <div className="Wrapper">
            <div ref={widgetRef} className={getClassName(isSuperRealm, realmSettings?.data?.avatarLogo)}>
                {widgetReady && !isLoading && !isFetching && (
                    <FormControl variant="outlined" className="locale-button-wrapper">
                        <Stack
                            direction={'row'}
                            alignItems={'center'}
                            onClick={handleIconClick}
                            className="cursor-pointer"
                            spacing={1}
                        >
                            <Typography style={{ marginLeft: 8, textTransform: 'uppercase', fontWeight: 600 }}>
                                {locale ??
                                    window.localStorage.getItem('loginPageLocale') ??
                                    window.localStorage.getItem('globalLocale') ??
                                    DEFAULT_LANGUAGE}
                            </Typography>
                            <Language />
                        </Stack>
                        <Popper open={open} anchorEl={anchorEl} placement="bottom-end" disablePortal>
                            <ClickAwayListener onClickAway={handleClose}>
                                <div className="box-shadow bg-white border-radius-4 black-500 margin-top-16 ">
                                    {languagesData?.data?.map((lang: any) => (
                                        <MenuItem
                                            key={lang?.value}
                                            onClick={() => {
                                                i18n.changeLanguage(lang?.value) as any;
                                                window.localStorage.setItem('tempLocale', lang?.value);
                                                window.localStorage.setItem('loginPageLocale', lang?.value);
                                                dispatch(setLocale(lang?.value));
                                                setOpen(false);
                                            }}
                                            className="padding-right-16"
                                        >
                                            {lang?.label}
                                        </MenuItem>
                                    ))}
                                </div>
                            </ClickAwayListener>
                        </Popper>
                    </FormControl>
                )}
            </div>
            <NeedHelpScreen isOpen={isContactScreenOpen} handleCloseModal={(): void => setIsContactScreenOpen(false)} />
            <PrivacyPolicy ref={policyRef} />
            {(!widgetReady || isLoading || isFetching || isCheckingExistingSession) && (
                <BackdropLoader isOpen showDefault={false} />
            )}
        </div>
    );
};
